import classNames from 'classnames';
import { TFunction } from 'i18next';
import React from 'react';

import { useAuth } from '../../app/AuthorizationProvider';
import { EllipsisContent } from '../../components/EllipsisContent';
import { ViewIcon } from '../../components/ListIcons/ViewIcon';
import { SortedBulkLabels } from '../../components/SortedBulkLabels';
import { AdherenceHistoryItem, Team } from '../../graphql/components';
import { formatSecondsToReadableForm } from '../../utils/dateRange';
import { formatNameOfPerson, nameOf } from '../../utils/helpers';
import { AdherenceHistoryBadge } from './AdherenceHistoryBadge';
import { getListItemTag } from './constants';

export interface AdherenceHistoryListProps {
  adherenceHistory: AdherenceHistoryItem[];
  hasViewPermission: boolean;
  hasPermission: boolean;
  isOneDaySelected?: boolean;
  onViewAdherenceHistoryItem: (h: AdherenceHistoryItem) => void;
  t: TFunction;
  activeId?: string;
  teams: Record<string, Team>;
}

interface AdherenceHistoryListRowProps {
  adherenceHistoryItem: AdherenceHistoryItem;
  hasPermission: boolean;
  hasViewPermission: boolean;
  index: number;
  isOneDaySelected?: boolean;
  locale: string;
  onViewAdherenceHistoryItem: (h: AdherenceHistoryItem) => void;
  t: TFunction;
  active?: boolean;
  teams: Record<string, Team>;
}

const AdherenceHistoryListRow: React.FC<AdherenceHistoryListRowProps> = ({
  adherenceHistoryItem,
  hasPermission,
  hasViewPermission,
  index,
  isOneDaySelected,
  locale,
  onViewAdherenceHistoryItem,
  t,
  teams,
  active = false,
}) => (
  <tr
    key={adherenceHistoryItem.agentId}
    className={classNames({
      'table-active': active,
    })}
  >
    <EllipsisContent
      as="td"
      data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('agentSurname'))}
      className="font-weight-bold"
    >
      {adherenceHistoryItem.agentSurname === 'Unknown' && adherenceHistoryItem.agentName === 'Unknown'
        ? t('person.unknown')
        : formatNameOfPerson(adherenceHistoryItem.agentName, adherenceHistoryItem.agentSurname)}
    </EllipsisContent>
    <td className="col-wrap" data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('teamIds'))}>
      <SortedBulkLabels
        items={adherenceHistoryItem?.teamIds?.map((teamId) => teams[teamId]).filter(Boolean)}
        label={t('team.team', { count: 2 })}
      />
    </td>
    <td data-test-id={getListItemTag(index, 'scheduledTime')} className="text-center">
      {formatSecondsToReadableForm(adherenceHistoryItem.scheduledTime, t)}
    </td>
    <td
      data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('scheduledWorkingTime'))}
      className="text-center"
    >
      {formatSecondsToReadableForm(adherenceHistoryItem.scheduledWorkingTime, t)}
    </td>
    <td data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('outOfAdhTimeTotal'))} className="text-center">
      {formatSecondsToReadableForm(adherenceHistoryItem.outOfAdhTimeTotal, t)}
    </td>
    <td
      data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('outOfAdhEventsCount'))}
      className="text-center"
    >
      <AdherenceHistoryBadge kind="primary">{adherenceHistoryItem.outOfAdhEventsCount}</AdherenceHistoryBadge>
    </td>
    <td
      data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('inAdherencePercentage'))}
      className="text-center"
    >
      {`${(adherenceHistoryItem.inAdherencePercentage / 100).toLocaleString(locale, {
        maximumFractionDigits: 2,
        style: 'percent',
      })}`}
    </td>
    <td
      data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('conformancePercentage'))}
      className="text-center"
    >
      {`${(adherenceHistoryItem.conformancePercentage / 100).toLocaleString(locale, {
        maximumFractionDigits: 2,
        style: 'percent',
      })}`}
    </td>
    <td data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('lateArrivalsCount'))} className="text-center">
      <AdherenceHistoryBadge displayValueAsWord={isOneDaySelected} kind="danger">
        {adherenceHistoryItem.lateArrivalsCount}
      </AdherenceHistoryBadge>
    </td>
    <td data-test-id={getListItemTag(index, nameOf<AdherenceHistoryItem>('earlyLeavesCount'))} className="text-center">
      <AdherenceHistoryBadge displayValueAsWord={isOneDaySelected} kind="danger">
        {adherenceHistoryItem.earlyLeavesCount}
      </AdherenceHistoryBadge>
    </td>
    {hasPermission && (
      <td className="list-actions">
        {hasViewPermission && (
          <ViewIcon
            testId={getListItemTag(index, 'view')}
            {...(adherenceHistoryItem.adherenceRawDataAvailable
              ? {
                  // Detail view should be enabled when there is at least 1 day within range having raw data available
                  onClick: () => onViewAdherenceHistoryItem(adherenceHistoryItem),
                }
              : {
                  // Detail view should be disabled
                  enabled: false,
                  tooltipMessageKey: 'scheduleAdherence.noDetailedAdherenceDataAvailable',
                })}
          />
        )}
      </td>
    )}
  </tr>
);

export const AdherenceHistoryList: React.FC<AdherenceHistoryListProps> = ({ activeId, adherenceHistory, ...rest }) => {
  const { locale } = useAuth();

  return (
    <tbody>
      {adherenceHistory.map((adherenceHistoryItem, index) => (
        <AdherenceHistoryListRow
          active={adherenceHistoryItem.agentId === activeId}
          adherenceHistoryItem={adherenceHistoryItem}
          index={index}
          key={adherenceHistoryItem.agentId}
          locale={locale}
          {...rest}
        />
      ))}
    </tbody>
  );
};
